import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertCircle, Check, Loader2, RefreshCw, X } from 'lucide-react'
import { Button } from './Button.js'
import { SLOW_AFTER_MS, STUCK_AFTER_MS, advanceSession, formatSeconds, lineOf, type LoadingLine, type LoadingSession, type LoadingSource } from './loadingLines.js'

/** What one view reports: whether it is shown, which visit it is on, its title for the window and its reads as lines. */
interface OwnerEntry {
  shown: boolean
  visit: string
  title: string | null
  subtitle: string | null
  rank: number
  lines: readonly LoadingLine[]
}

interface Registry {
  report: (owner: string, entry: OwnerEntry, retries: Readonly<Record<string, () => unknown>>) => void
  remove: (owner: string) => void
}

const LoadingWindowContext = createContext<Registry | null>(null)

/** A read that was in before the window began (another view had it) reads as already here, not "0 s". */
const ALREADY_HERE_MS = 50

/** How often an open or waiting window looks at the clock: its seconds, its pause before showing and its close. */
const TICK_MS = 250

function sameEntry(a: OwnerEntry, b: OwnerEntry): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** Every shown view's lines, the first view to name a read keeping it. */
function shownLines(owners: ReadonlyMap<string, OwnerEntry>): LoadingLine[] {
  const seen = new Set<string>()
  const lines: LoadingLine[] = []
  for (const entry of owners.values()) {
    if (!entry.shown) {
      continue
    }
    for (const line of entry.lines) {
      if (!seen.has(line.key)) {
        seen.add(line.key)
        lines.push(line)
      }
    }
  }
  return lines
}

/** The title of the most particular view shown (the board over the month over the dashboard), and a subtitle from any that gives one. */
function headingOf(owners: ReadonlyMap<string, OwnerEntry>): { title: string; subtitle: string | null } {
  const shown = [...owners.values()].filter((entry) => entry.shown)
  const titled = shown.filter((entry) => entry.title !== null).sort((a, b) => b.rank - a.rank)[0]
  const subtitled = shown.filter((entry) => entry.subtitle !== null).sort((a, b) => b.rank - a.rank)[0]
  return { title: titled?.title ?? 'Loading', subtitle: subtitled?.subtitle ?? null }
}

/**
 * Holds the loading window for a shell (the loading standard, design screens 9 and 10, 26 October 2026). Views report
 * their reads with `useLoadingWindow`; the provider shows one window for the views on screen, after `SHOW_AFTER_MS`,
 * only while two or more reads are going, and closes it when they are in. Views mounted but hidden (the Dashboard's
 * other tabs) report as not shown and never open it.
 */
export function LoadingWindowProvider({ children }: { children: ReactNode }) {
  const [owners, setOwners] = useState<ReadonlyMap<string, OwnerEntry>>(() => new Map())
  const retries = useRef(new Map<string, Readonly<Record<string, () => unknown>>>())
  const [session, setSession] = useState<LoadingSession | null>(null)
  const [now, setNow] = useState(() => Date.now())

  const registry = useMemo<Registry>(
    () => ({
      report: (owner, entry, ownerRetries) => {
        retries.current.set(owner, ownerRetries)
        setOwners((current) => {
          const previous = current.get(owner)
          if (previous && sameEntry(previous, entry)) {
            return current
          }
          return new Map(current).set(owner, entry)
        })
      },
      remove: (owner) => {
        retries.current.delete(owner)
        setOwners((current) => {
          if (!current.has(owner)) {
            return current
          }
          const next = new Map(current)
          next.delete(owner)
          return next
        })
      },
    }),
    [],
  )

  const lines = useMemo(() => shownLines(owners), [owners])
  const visits = useMemo(() => Object.fromEntries([...owners.entries()].filter(([, entry]) => entry.shown).map(([owner, entry]) => [owner, entry.visit])), [owners])

  useEffect(() => {
    setSession((current) => advanceSession(current, visits, lines, Date.now()))
  }, [visits, lines, now])

  const ticking = session?.phase === 'waiting' || session?.phase === 'open'
  useEffect(() => {
    if (!ticking) {
      return
    }
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS)
    return () => window.clearInterval(timer)
  }, [ticking])

  const close = useCallback(() => setSession((current) => (current ? { ...current, phase: 'closed' } : current)), [])
  const retry = useCallback((keys: readonly string[]) => {
    for (const ownerRetries of retries.current.values()) {
      for (const key of keys) {
        void ownerRetries[key]?.()
      }
    }
  }, [])

  return (
    <LoadingWindowContext.Provider value={registry}>
      {children}
      {session?.phase === 'open' && <LoadingWindowView heading={headingOf(owners)} lines={lines} session={session} now={now} onClose={close} onRetry={retry} />}
    </LoadingWindowContext.Provider>
  )
}

interface UseLoadingWindowOptions {
  /** Whether the view is on screen; a view mounted but hidden reports its reads without opening the window. */
  shown: boolean
  /** Which visit this is (the facility, the month): a change starts a new window. */
  visit: string
  /** The window's title while this is the most particular view shown ("Opening the board"). */
  title?: string
  /** A line under the title ("North Campus · November"). */
  subtitle?: string
  /** Which view's title wins when several are shown: the dashboard 0, a tab 1, a detail inside it 2. */
  rank?: number
  /** The reads the view waits on, in the order the window lists them. */
  sources: readonly LoadingSource[]
}

/**
 * Reports a view's reads to the loading window. A no-op outside a `LoadingWindowProvider`. The view still draws its
 * own skeletons; the window only says what is on its way.
 */
export function useLoadingWindow({ shown, visit, title, subtitle, rank = 0, sources }: UseLoadingWindowOptions): void {
  const registry = useContext(LoadingWindowContext)
  const owner = useId()
  const lines = sources.map(lineOf).filter((line): line is LoadingLine => line !== null)
  const entry: OwnerEntry = { shown, visit, title: title ?? null, subtitle: subtitle ?? null, rank, lines }
  const ownerRetries = Object.fromEntries(sources.map((source) => [source.key, () => source.query.refetch()]))
  // Every render: the provider keeps the entry only when it changed.
  useEffect(() => {
    registry?.report(owner, entry, ownerRetries)
  })
  useEffect(() => () => registry?.remove(owner), [registry, owner])
}

interface LoadingWindowViewProps {
  heading: { title: string; subtitle: string | null }
  lines: readonly LoadingLine[]
  session: LoadingSession
  now: number
  onClose: () => void
  onRetry: (keys: readonly string[]) => void
}

/** The window itself (design screen 9): a line per read turning from a spinner to a tick with its time, a count, and Keep working. */
function LoadingWindowView({ heading, lines, session, now, onClose, onRetry }: LoadingWindowViewProps) {
  const keepWorking = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const before = document.activeElement
    keepWorking.current?.querySelector('button')?.focus()
    return () => {
      if (before instanceof HTMLElement && document.contains(before)) {
        before.focus()
      }
    }
  }, [])
  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        onClose()
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [onClose])

  const took = (line: LoadingLine) => (line.state === 'loading' ? now : (session.ended[line.key] ?? now)) - (session.started[line.key] ?? session.startedAt)
  const ready = lines.filter((line) => line.state === 'done' || line.state === 'answered').length
  const failed = lines.filter((line) => line.state === 'failed')
  const stuck = lines.filter((line) => line.state === 'loading' && took(line) >= STUCK_AFTER_MS)
  const count = `${ready} of ${lines.length} ready${failed.length > 0 ? ` · ${failed.length} could not be loaded` : ''}`

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-ink/40 px-4 py-[6vh]">
      <div role="dialog" aria-modal="true" aria-labelledby="loading-window-title" className="flex max-h-full w-full max-w-[520px] flex-col gap-3.5 rounded-lg bg-white px-6 pb-5 pt-5 shadow-xl">
        <div className="flex flex-col gap-1">
          {heading.subtitle && <span className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{heading.subtitle}</span>}
          <h2 id="loading-window-title" className="text-lg font-black text-ink">
            {heading.title}
          </h2>
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between text-xs font-bold text-ink">
            <span role="status" aria-live="polite">
              {count}
            </span>
            <span className="text-[11px] tabular-nums text-ink-mid">{formatSeconds(now - session.startedAt)}</span>
          </div>
          <div aria-hidden="true" className="h-1.5 overflow-hidden rounded-full bg-silver-lt">
            <div className="h-full bg-marine transition-[width] duration-300" style={{ width: `${lines.length > 0 ? (ready / lines.length) * 100 : 0}%` }} />
          </div>
        </div>
        {/* Many reads scroll here, so Keep working stays in sight on a short screen. */}
        <ul className="-mx-1 flex min-h-0 flex-col overflow-y-auto px-1">
          {lines.map((line, index) => (
            <LineRow key={line.key} line={line} ms={took(line)} last={index === lines.length - 1} onRetry={() => onRetry([line.key])} />
          ))}
        </ul>
        <div className="flex flex-wrap items-center gap-2.5 border-t border-marine/15 pt-3.5">
          <span className="min-w-[200px] flex-1 text-[11px] leading-snug text-ink-mid">
            {failed.length > 0 ? 'The rest of the screen is ready. Try again, or close this and carry on.' : 'Closes by itself when everything is in. Close it to keep working; each part shows as soon as it arrives.'}
          </span>
          {stuck.length > 0 && (
            <Button icon={RefreshCw} variant="secondary" size="sm" onClick={() => onRetry(stuck.map((line) => line.key))}>
              Try again
            </Button>
          )}
          <span ref={keepWorking} className="contents">
            <Button icon={X} variant="secondary" size="sm" onClick={onClose}>
              Keep working
            </Button>
          </span>
        </div>
      </div>
    </div>
  )
}

function LineRow({ line, ms, last, onRetry }: { line: LoadingLine; ms: number; last: boolean; onRetry: () => void }) {
  const slow = line.state === 'loading' && ms >= SLOW_AFTER_MS
  return (
    <li className={`grid grid-cols-[18px_1fr_auto] items-start gap-3 py-2.5 ${last ? '' : 'border-b border-marine/10'}`}>
      {line.state === 'loading' ? (
        <Loader2 aria-hidden="true" strokeWidth={2.5} className="mt-px h-[18px] w-[18px] text-marine motion-safe:animate-spin" />
      ) : line.state === 'failed' ? (
        <AlertCircle aria-hidden="true" strokeWidth={2.5} className="mt-px h-[18px] w-[18px] text-coral" />
      ) : (
        <Check aria-hidden="true" strokeWidth={2.5} className={`mt-px h-[18px] w-[18px] ${line.state === 'answered' ? 'text-silver' : 'text-green'}`} />
      )}
      <div className="min-w-0">
        <div className={`text-[13px] font-black ${line.state === 'failed' ? 'text-coral' : 'text-ink'}`}>{line.state === 'failed' ? `${line.label}: could not be loaded` : line.label}</div>
        {(line.note || slow) && <div className="mt-0.5 text-[11px] leading-snug text-ink-mid">{line.note ?? line.slowNote ?? 'Still working. This one takes longer.'}</div>}
      </div>
      {line.state === 'failed' ? (
        <Button icon={RefreshCw} variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : (
        <span className={`whitespace-nowrap text-[11px] font-bold tabular-nums ${line.state === 'loading' ? 'text-marine' : 'text-ink-mid'}`}>{line.state === 'answered' ? 'nothing yet' : line.state === 'done' && ms < ALREADY_HERE_MS ? 'already here' : formatSeconds(ms)}</span>
      )}
    </li>
  )
}
